// Dependencies
import { Component } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';
import { Tooltip } from 'react-tooltip';

import Form from 'components/atoms/Form';
import Icon from 'components/atoms/Icon';
import LoaderSpinner from 'components/atoms/LoaderSpinner';
import { bindActionCreators, Dispatch } from 'redux';
import { locateDistance, profileHistogram } from 'services/profile';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    SetSystemLoadingAction,
    setSystemLoadingAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import {
    setLayoutAction,
    SetLayoutAction,
    SetOpenModelFunctionAction,
    setOpenModelFunctionAction,
} from 'store/actions/layoutActions';
import { ChainageType, OpenModal, PortType, ProfileDataType, RootState } from 'types';
import { MapControlsInterface, ProfileControlsInterface, VoidInterface } from 'types/functions';
import { FilterObjectType } from 'types/search';
import { ActionsType } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import { translate } from 'utils/translate';
import { metersToNauticalMiles } from 'utils/unit';

import ProfileHistogramChart from '../ProfileHistogramChart';

interface ProfileHistogramModelComponentProps {
    active?: boolean;

    id?: string;
    data: any;
    actions: ActionsType | undefined;

    searchObject: FilterObjectType;
    port: PortType;
    dateTimeFormat: DateTimeFormat;
    setErrorHandlerAction: SetErrorHandlerAction;
    showMessageAction: ShowMessageAction;
    setSystemLoadingAction: SetSystemLoadingAction;
    openModelFunction: OpenModal;

    profileControls: ProfileControlsInterface;
    setOpenModelFunctionAction: SetOpenModelFunctionAction;
    setLayoutAction: SetLayoutAction;
    mapControls: MapControlsInterface;
}

interface ProfileHistogramModelComponentState {
    active: boolean;

    profileComplated: boolean;
    sourceSearchObject: FilterObjectType;
    searchObject: FilterObjectType;
    analysisData: ProfileDataType;
    unit: string;
    transits: number[];
    results: any;
    exportFunctions: VoidInterface[];
    histogramIndex: number;
    chainage: ChainageType[];
    profileId: string;
    profileResults: any;
    updateFunctions: any;
    plotLineValues: boolean;
    loading: boolean;
}

class ProfileHistogramModel extends Component<
    ProfileHistogramModelComponentProps,
    ProfileHistogramModelComponentState
> {
    public geometry;
    constructor(props: ProfileHistogramModelComponentProps) {
        super(props);
        this.state = {
            unit: 'point',
            chainage: [],
            transits: props.data.transits,
            histogramIndex: props.data.histogramIndex,
            profileId: props.data.profileId,
            analysisData: props.data.analysisData,
            results: null,

            active: false,

            exportFunctions: [],
            profileComplated: false,
            loading: true,
            sourceSearchObject: clone(props.searchObject),
            searchObject: clone(props.searchObject),
            profileResults: props.data.results,
            updateFunctions: [],
            plotLineValues: true,
        };
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(
        props: ProfileHistogramModelComponentProps,
        state: ProfileHistogramModelComponentState
    ) {
        if (props.active !== state.active) {
            return {
                active: props.active,
            };
        }

        return null;
    }

    componentDidMount(): void {
        this.getHistogram();
    }

    componentDidUpdate(
        prevProps: Readonly<ProfileHistogramModelComponentProps>,
        prevState: Readonly<ProfileHistogramModelComponentState>,
        snapshot?: any
    ): void {
        if (checkDifferent(this.state.histogramIndex, prevState.histogramIndex)) {
            this.getHistogram();
        }
    }

    getHistogram = () => {
        // eslint-disable-next-line prefer-const
        let { analysisData, transits, profileId, histogramIndex } = this.state;

        if (!analysisData) {
            return;
        }

        this.setState(
            {
                loading: true,
            },
            () => {
                const points = {};
                let binLength = null;
                Object.keys(this.props.data.results.pilotagePlanAnalysis).forEach((item, i) => {
                    binLength = this.props.data.results.pilotagePlanAnalysis[item].binLength;
                    const data = this.props.data.results.pilotagePlanAnalysis[item].data;
                    points[item] = {
                        last: data.length,
                        min: data[histogramIndex].at(0),
                        low: data[histogramIndex].at(1),
                        q1: data[histogramIndex].at(2),
                        median: data[histogramIndex].at(3),
                        q3: data[histogramIndex].at(4),
                        high: data[histogramIndex].at(5),
                        max: data[histogramIndex].at(6),
                    };
                });

                this.profileHistogramTry(histogramIndex, analysisData, transits, points, profileId, 0);
            }
        );
    };

    profileHistogramTry = (index, analysisData, transits, points, id, retry) => {
        profileHistogram(this.props.port.id, {
            transits: transits,
            request: 'json',
            histogram: {
                index: [index],
                points,
            },
            analysisData: analysisData,
            retry: retry,
            id: id,
        })
            .then((res) => {
                this.setState({
                    results: res.items,
                    loading: false,
                    histogramIndex: index,
                });
            })
            .catch((error) => {
                if (retry < 5 && error?.response?.status === 504) {
                    if (retry === 1) {
                        this.props.showMessageAction({
                            messageType: 'INFO',
                            message: translate(`Preparing data will take some time please wait.`),
                        });
                    }

                    this.profileHistogramTry(index, analysisData, transits, points, id, retry + 1);
                } else {
                    if (error?.response?.status === 504) {
                        this.setState({ loading: false });
                        this.props.showMessageAction({
                            messageType: 'ERROr',
                            message: translate(`Couldn't complete this profile`),
                        });
                    } else {
                        this.setState({ loading: false });
                        this.props.setErrorHandlerAction(error);
                    }
                }
            });
    };

    next = () => {
        const firstkey = Object.keys(this.state.results.pilotagePlanAnalysis)[0];
        const firstResults = this.state.profileResults.pilotagePlanAnalysis[firstkey];
        if (this.state.histogramIndex === firstResults?.label.length - 1) {
            return false;
        }
        this.setState({
            histogramIndex: this.state.histogramIndex + 1,
        });
        this.props.data.handleHistogramIndex(this.state.histogramIndex);
    };

    back = () => {
        if (this.state.histogramIndex === 0) {
            return false;
        }

        this.setState({
            histogramIndex: this.state.histogramIndex - 1,
        });
        this.props.data.handleHistogramIndex(this.state.histogramIndex);
    };

    handleCheckboxChange = () => {
        this.setState({
            plotLineValues: !this.state.plotLineValues,
        });
    };

    locate = () => {
        const chainage =
            this.state.profileResults.pilotagePlanAnalysis[
                Object.keys(this.state.profileResults.pilotagePlanAnalysis)[0]
            ].label[this.state.histogramIndex];

        locateDistance(this.props.port.id, {
            request: 'json',
            locate: {
                distance: chainage,
                binLength:
                    this.state.profileResults.pilotagePlanAnalysis[
                        Object.keys(this.state.profileResults.pilotagePlanAnalysis)[0]
                    ].binLength,
            },
            analysisData: this.state.analysisData,
        })
            .then((res) => {
                const line = this.geometry.transformerGEOJSON(this.geometry.parseWKB(res.items.line));
                const startPoint = this.geometry.transformerGEOJSON({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: res.items.start },
                });
                const endPoint = this.geometry.transformerGEOJSON({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: res.items.end },
                });

                this.props.mapControls.chart?.createMarkingLayer([line, startPoint, endPoint], true, true);
                //this.props.activeToolFunction(['chart'], ['profile'], [], true, {});

                this.props.setLayoutAction([
                    {
                        action: 'UPDATE_SECTION',
                        data: { name: 'chart', state: true, display: true, disable: false },
                    },
                ]);

                this.handleFormCancel();
            })
            .catch((errors) => {
                this.props.setErrorHandlerAction(errors);
            });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    render() {
        const routeName = this.props.port.port_areas?.find((x) => {
            return x.key === this.state.analysisData?.pilotagePlanAnalysis?.pilotagePlan?.route;
        });

        if (this.state.loading) {
            return (
                <Form className="bg-white shadow p-4 w-full rounded" style={{ width: '800px' }}>
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Profile Histogram')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    <LoaderSpinner className="my-10 mx-20" variant="table"></LoaderSpinner>
                </Form>
            );
        }

        if (!this.state.results || this.state.loading) {
            return;
        }
        const firstkey = Object.keys(this.state.results?.pilotagePlanAnalysis)[0];
        const firstResults = this.state.profileResults?.pilotagePlanAnalysis[firstkey];
        const pointName = firstResults?.plotLines?.find((x) => {
            return x[1] === this.state.histogramIndex;
        });
        const chainage = firstResults?.label[this.state.histogramIndex];

        return (
            <Form className="bg-white shadow p-4 w-full rounded" style={{ width: '800px' }}>
                <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                    <div className="text-lg leading-6 font-bold">{translate('Profile Histogram')} </div>
                    <div onTouchEnd={this.handleFormCancel} onClick={this.handleFormCancel} className="cursor-pointer">
                        <Icon>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M18 6l-12 12" />
                            <path d="M6 6l12 12" />
                        </Icon>
                    </div>
                </div>
                <div className="p-2 h-10 flex justify-between   border-b border-gray-300 ">
                    {pointName ? (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: translate('distribution data point name', {
                                    transits: this.state.results.pilotagePlanAnalysis[
                                        Object.keys(this.state.results.pilotagePlanAnalysis)[0]
                                    ][0].data.histogram_stats.reduce(
                                        (total, current) => total + parseFloat(current.value),
                                        0
                                    ),
                                    route: routeName?.name,
                                    pointName: pointName[0],
                                }),
                            }}
                        ></div>
                    ) : (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: translate('distribution data chainage', {
                                    transits: this.state.results.pilotagePlanAnalysis[
                                        Object.keys(this.state.results.pilotagePlanAnalysis)[0]
                                    ][0].data.histogram_stats.reduce(
                                        (total, current) => total + parseFloat(current.value),
                                        0
                                    ),
                                    route: routeName?.name,
                                    nm: metersToNauticalMiles(parseFloat(chainage)),
                                }),
                            }}
                        ></div>
                    )}

                    <div className="flex justify-between gap-2">
                        <div data-tooltip-id="valueProfileHistogramModelTooltip" onClick={this.handleCheckboxChange}>
                            <Icon
                                currentColor={!this.state.plotLineValues ? '#ddd' : 'currentColor'}
                                className="w-6 h-6 cursor-pointer"
                            >
                                <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z"></path>
                                <path d="M9 15.075l6 -6"></path>
                                <path d="M9 9.105v.015"></path>
                                <path d="M15 15.12v.015"></path>
                            </Icon>
                            <Tooltip
                                html={`${translate('show values')}`}
                                variant="dark"
                                place="left"
                                id="valueProfileHistogramModelTooltip"
                            />
                        </div>

                        <div onClick={this.locate} data-tooltip-id="locateProfileHistogramModelTooltip">
                            <Icon className="w-6 h-6 cursor-pointer">
                                <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
                                <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"></path>
                            </Icon>
                            <Tooltip
                                html={`${translate('Show on chart')}`}
                                variant="dark"
                                place="left"
                                id="locateProfileHistogramModelTooltip"
                            />
                        </div>

                        <div onClick={this.back} data-tooltip-id="nextProfileHistogramModelTooltip">
                            <Icon
                                currentColor={this.state.histogramIndex === 0 ? '#ddd' : 'currentColor'}
                                className="w-6 h-6 cursor-pointer"
                            >
                                <path d="M15 6l-6 6l6 6"></path>
                            </Icon>
                            <Tooltip
                                html={`${translate('previous bin')}`}
                                variant="dark"
                                place="left"
                                id="nextProfileHistogramModelTooltip"
                            />
                        </div>
                        <div onClick={this.next} data-tooltip-id="backProfileHistogramModelTooltip">
                            <Icon
                                currentColor={
                                    this.state.histogramIndex === firstResults?.label.length - 1
                                        ? '#ddd'
                                        : 'currentColor'
                                }
                                className="w-6 h-6 cursor-pointer "
                            >
                                <path d="M9 6l6 6l-6 6"></path>
                            </Icon>
                            <Tooltip
                                html={`${translate('Next bin')}`}
                                place="left"
                                variant="dark"
                                id="backProfileHistogramModelTooltip"
                            />
                        </div>
                    </div>
                </div>
                <Scrollbars
                    renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
                    renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
                    style={{
                        height: 'calc( 100vh - 200px)',
                    }}
                >
                    {this.state.results?.pilotagePlanAnalysis && (
                        <div className="px-4 mt-3">
                            <div className="grid grid-cols-1 gap-y-3">
                                {Object.keys(this.state.results.pilotagePlanAnalysis).map((item, i) => {
                                    return (
                                        <div key={i}>
                                            <ProfileHistogramChart
                                                plotLineValues={this.state.plotLineValues}
                                                for={item}
                                                data={this.state.results.pilotagePlanAnalysis[item]}
                                            ></ProfileHistogramChart>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </Scrollbars>
            </Form>
        );
    }
}

const profileHistogramStateToProps = (state: RootState) => ({
    searchObject: state.layout.searchObject,

    mapControls: state.map.mapControls,
    port: state.port.port,
    profileControls: state.layout.profileControls,
    dateTimeFormat: state.port.dateTimeFormat,
    openModelFunction: state.layout.openModelFunction,
});

const profileHistogramDispatchToProps = (dispatch: Dispatch) => ({
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setOpenModelFunctionAction: bindActionCreators(setOpenModelFunctionAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(profileHistogramStateToProps, profileHistogramDispatchToProps)(ProfileHistogramModel);
